export const displayLocale = "de-AT";

const structuralLocale = "en-CA";
const dateInputFormatter = new Intl.DateTimeFormat(displayLocale, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});

function validISODate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function formatISODate(value: string) {
  return validISODate(value) ? dateInputFormatter.format(new Date(`${value}T00:00:00.000Z`)) : value;
}

const dateInputParts = new Intl.DateTimeFormat(displayLocale, {
  day: "numeric",
  month: "numeric",
  year: "numeric",
  timeZone: "UTC",
}).formatToParts(new Date("2006-11-22T00:00:00.000Z"));

export function parseDisplayDate(value: string) {
  const trimmed = value.trim();
  if (validISODate(trimmed)) return trimmed;
  const numbers = trimmed.match(/\d+/g)?.map(Number) ?? [];
  const fields = dateInputParts.filter((part) => part.type === "day" || part.type === "month" || part.type === "year").map((part) => part.type);
  if (numbers.length !== 3 || fields.length !== 3) return "";
  const parts = Object.fromEntries(fields.map((field, index) => [field, numbers[index]])) as Record<"day" | "month" | "year", number>;
  const iso = `${String(parts.year).padStart(4, "0")}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
  return validISODate(iso) ? iso : "";
}

export function localizedDatePlaceholder() {
  return dateInputParts.map((part) => {
    if (part.type === "day") return "TT";
    if (part.type === "month") return "MM";
    if (part.type === "year") return "JJJJ";
    return part.value;
  }).join("");
}

export function formatDateTime(value: Date | number | string, options: Intl.DateTimeFormatOptions = {}) {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat(displayLocale, options).format(date) : "–";
}

export function formatNumber(value: number, options: Intl.NumberFormatOptions = {}) {
  return new Intl.NumberFormat(displayLocale, options).format(value);
}

export function formatChartDay(value: Date | number, timeZone: string) {
  const date = value instanceof Date ? value : new Date(value);
  const weekday = new Intl.DateTimeFormat(displayLocale, { weekday: "short", timeZone }).format(date).replace(/\.$/, "");
  const calendarDate = new Intl.DateTimeFormat(displayLocale, { day: "numeric", month: "numeric", timeZone }).format(date);
  return `${weekday} - ${calendarDate}`;
}

export function structuralDateTimeParts(date: Date, timeZone: string) {
  return Object.fromEntries(new Intl.DateTimeFormat(structuralLocale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).formatToParts(date).map((part) => [part.type, part.value]));
}

export function structuralDateParts(date: Date, timeZone: string) {
  return Object.fromEntries(new Intl.DateTimeFormat(structuralLocale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).formatToParts(date).map((part) => [part.type, part.value]));
}

export function zonedDateParts(date: Date, timeZone: string) {
  try {
    const parts = structuralDateTimeParts(date, timeZone);
    return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day), hour: Number(parts.hour), minute: Number(parts.minute), second: Number(parts.second) };
  } catch {
    return null;
  }
}

export function timeSeconds(value: string) {
  if (value === "24:00") return 24 * 60 * 60;
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return Number.NaN;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60 ? hours * 3600 + minutes * 60 : Number.NaN;
}

// Resolves a wall-clock date and time in timeZone, including "24:00" and DST days.
export function dateAtTime(dateValue: string, time: string, timeZone: string) {
  const seconds = timeSeconds(time);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue) || !Number.isFinite(seconds)) return null;
  const [year, month, day] = dateValue.split("-").map(Number);
  const target = new Date(Date.UTC(year, month - 1, day));
  target.setUTCSeconds(target.getUTCSeconds() + seconds);
  const targetTimestamp = target.getTime();
  let candidateTimestamp = targetTimestamp;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = zonedDateParts(new Date(candidateTimestamp), timeZone);
    if (!parts) return null;
    const displayedTimestamp = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    if (displayedTimestamp === targetTimestamp) return new Date(candidateTimestamp);
    candidateTimestamp += targetTimestamp - displayedTimestamp;
  }
  return null;
}
